import {
    AttributeFunctionalType,
    AttributesMetadata,
    AttributeType,
    BUSINESS_RULES_FIELD_ID,
    ShipmentAttributes,
} from '../constants/constants'
import { EntityPreset } from '../services/entityPresetServices'
import { isTimeSeries } from './AttributeUtils'
import {
    isEntityReferenceValue,
    updateValuesForPayload,
} from './EntityAttributesUtils'

/** The identity attribute; every record needs its own value. */
const IDENTITY_ATTRIBUTE_ID = 'id'

/**
 * Attributes the form fills in from the Trip Number the moment one is chosen
 * (Shipment Name takes the trip's name). A template still stores them, because
 * the server requires Shipment Name, but they are not asked for in the collapsed
 * form and do not stop a template from being Quick create ready.
 */
export const TRIP_DERIVED_ATTRIBUTE_IDS: string[] = [
    ShipmentAttributes.ShipmentNameAttributeId,
]

/**
 * Types a preset template can never hold: relations, derived values and layout.
 * The device relation is Trip Number, which is always entered per shipment.
 */
const NON_TEMPLATABLE_DATA_TYPES: string[] = [
    AttributeType.DEVICE_RELATION,
    AttributeType.CUSTOM_ATTRIBUTE,
    AttributeType.SEPARATOR,
    AttributeType.RULESET,
    AttributeType.RULES_FIELD_TRIGGER,
    AttributeType.SEALINE,
    AttributeType.CREATION_DATETIME,
    AttributeType.SENSOR_SNAPSHOT_READINGS,
]

/** Minimal shape read from a form attribute; the form's own attributes carry more. */
export interface PresetSourceAttribute {
    id: string
    dataType: string
    attributeType?: string
    metadata?: Record<string, boolean>
    hide?: boolean
    disabled?: boolean
    required?: boolean
    value?: unknown
    /** The user typed in this field, so it must not disappear under them. */
    userEdited?: boolean
}

/**
 * Whether an attribute is allowed in a preset template at all, whoever is
 * looking. The server rejects the rest with a 400, so the "Save as preset
 * template" form leaves them out up front.
 */
export const isTemplatableAttribute = (
    attribute: PresetSourceAttribute,
): boolean => {
    if (!attribute?.id) {
        return false
    }
    if (
        attribute.id.toLowerCase() === IDENTITY_ATTRIBUTE_ID ||
        attribute.id === BUSINESS_RULES_FIELD_ID
    ) {
        return false
    }
    if (
        attribute.attributeType &&
        attribute.attributeType !== AttributeFunctionalType.Static
    ) {
        return false
    }
    if (isTimeSeries(attribute as never)) {
        return false
    }
    if (
        attribute.metadata?.[AttributesMetadata.UNIQUE] ||
        attribute.metadata?.[AttributesMetadata.UNIQUE_GLOBAL]
    ) {
        return false
    }
    return !NON_TEMPLATABLE_DATA_TYPES.includes(
        attribute.dataType?.toLowerCase(),
    )
}

const isBlank = (value: unknown): boolean =>
    value === null ||
    value === undefined ||
    value === '' ||
    (Array.isArray(value) && value.length === 0)

/**
 * Builds the `attributeValues` map for "Save as preset template" from the
 * create form. Only what the user filled in is saved, in the payload format the
 * create API takes; entity references are sent as plain ids. Fields the user
 * cannot edit (hidden, view-only) and everything `isTemplatableAttribute` rules
 * out are left out.
 */
export const buildPresetAttributeValues = (
    attributes: PresetSourceAttribute[],
): Record<string, unknown> => {
    const eligible = (attributes ?? []).filter(
        (attribute) =>
            isTemplatableAttribute(attribute) &&
            !attribute.hide &&
            !attribute.disabled,
    )
    const values: Record<string, unknown> = {}
    updateValuesForPayload(eligible).forEach((attribute) => {
        const { id, value } = attribute as {
            id: string
            value: unknown
        }
        if (isBlank(value)) {
            return
        }
        values[id] = isEntityReferenceValue(value) ? value.entityId : value
    })
    return values
}

/** Which of a scope's preset templates the create form should apply. */
export type PresetSelection = 'auto' | 'none' | string

/**
 * Picks the preset template to apply: the user's choice, else the scope's
 * default. `'none'` is an explicit Clear and always means legacy defaults.
 */
export const resolvePreset = (
    options: EntityPreset[],
    selection: PresetSelection,
): EntityPreset | null => {
    if (selection === 'none') {
        return null
    }
    const chosen = options.find((preset) => preset.id === selection)
    return chosen ?? options.find((preset) => preset.isDefault) ?? null
}

/**
 * Where a create form's preset templates live: the organisation scope when the
 * user chose "No Program" (their own org), else the program behind the virtual
 * org the Program step holds.
 */
export const getPresetScopeParams = (
    selectedOrgId: string,
    loggedInOrgId: string,
): { programId: string } | { virtualOrgId: string } =>
    selectedOrgId === loggedInOrgId
        ? { programId: 'null' }
        : { virtualOrgId: selectedOrgId }

/**
 * Where a preset template saved from the wizard applies, as the create and
 * update calls take it: the organisation when the user chose "No Program" (their
 * own org), else the virtual org the Program step holds, which the server maps
 * to its program.
 */
export const getPresetScopeBody = (
    selectedOrgId: string,
    loggedInOrgId: string,
): { programId: null } | { virtualOrgId: string } =>
    selectedOrgId === loggedInOrgId
        ? { programId: null }
        : { virtualOrgId: selectedOrgId }

/**
 * A preset template read from the API has entity references expanded for
 * display; the update call wants them as plain ids, so turn them back before
 * sending it.
 */
export const presetValuesToPayload = (
    values: Record<string, unknown>,
): Record<string, unknown> =>
    Object.fromEntries(
        Object.entries(values ?? {}).map(([id, value]) => [
            id,
            isEntityReferenceValue(value) ? value.entityId : value,
        ]),
    )

/**
 * A short, entity-agnostic preview of what a preset template holds, for the
 * manage list: referenced entities by name first, then plain values, up to
 * `limit`.
 */
export const summarizePresetValues = (
    values: Record<string, unknown>,
    limit = 3,
): string[] => {
    const entries = Object.values(values ?? {})
    const references = entries
        .filter(isEntityReferenceValue)
        .map((value) => value.uid ?? '')
    const plain = entries
        .filter(
            (value) => typeof value === 'string' || typeof value === 'number',
        )
        .map(String)
    return [...references, ...plain]
        .filter((text) => text.length > 0)
        .slice(0, limit)
}

/** A value as the read-only Quick create summary shows it, or '' when there is nothing to show. */
export const formatFormValue = (value: unknown): string => {
    if (value === null || value === undefined || value === '') {
        return ''
    }
    if (Array.isArray(value)) {
        return value.map(formatFormValue).filter(Boolean).join(', ')
    }
    if (typeof value === 'object') {
        const record = value as Record<string, unknown>
        const text =
            record.uid ??
            record.displayName ??
            record.name ??
            record.datetime ??
            record.value
        return text === undefined ? '' : formatFormValue(text)
    }
    return String(value)
}

export interface FormSummaryItem {
    id: string
    label: string
    text: string
}

/**
 * What Quick create shows for checking before Save: every visible attribute
 * that holds a value. Trip Number (the device relation) is the input, not part
 * of the summary.
 */
export const buildFormSummary = (
    attributes: (PresetSourceAttribute & { name?: string })[],
): FormSummaryItem[] =>
    (attributes ?? [])
        .filter(
            (attribute) =>
                !attribute.hide &&
                attribute.dataType?.toLowerCase() !==
                    AttributeType.DEVICE_RELATION,
        )
        .map((attribute) => ({
            id: attribute.id,
            label: attribute.name ?? attribute.id,
            text: formatFormValue(attribute.value),
        }))
        .filter((item) => item.text.length > 0)

/**
 * A preset template's name in Quick create's picker, which lists every scope in
 * one list, so the scope and any caveat travel with the name.
 */
export const getPresetOptionLabel = (
    preset: Pick<
        EntityPreset,
        | 'name'
        | 'programName'
        | 'isDefault'
        | 'quickCreateReady'
        | 'missingRequiredAttributeIds'
    >,
    labels: {
        organization: string
        default: string
        /** Leave out to not flag templates that Quick create cannot use. */
        needsFullForm?: string
    },
): string => {
    const notes = [
        preset.isDefault ? labels.default : '',
        labels.needsFullForm && !isQuickCreateReady(preset)
            ? labels.needsFullForm
            : '',
    ].filter(Boolean)
    const scope = preset.programName ?? labels.organization
    return `${preset.name} - ${scope}${notes.length ? ` (${notes.join(', ')})` : ''}`
}

/**
 * A value only this record can have: a Unique / Unique Global attribute, the
 * identity attribute, or the device relation (Trip Number). A preset template
 * can never hold these, so the user always enters them.
 */
const isPerRecordAttribute = (attribute: PresetSourceAttribute): boolean =>
    !!attribute.metadata?.[AttributesMetadata.UNIQUE] ||
    !!attribute.metadata?.[AttributesMetadata.UNIQUE_GLOBAL] ||
    attribute.id?.toLowerCase() === IDENTITY_ATTRIBUTE_ID ||
    attribute.dataType?.toLowerCase() === AttributeType.DEVICE_RELATION

/**
 * The fields the create form still asks for once a preset template is applied:
 * the ones only this record can have, plus any required field the preset
 * template left empty (without it Save could not succeed). Everything else the
 * preset template supplied, or deliberately left blank, so it stays out of the
 * way. With no preset template applied the whole form is shown.
 */
export const selectFieldsToFill = <T extends PresetSourceAttribute>(
    attributes: T[],
): T[] =>
    (attributes ?? []).filter((attribute) => {
        if (attribute.hide) {
            return false
        }
        if (isPerRecordAttribute(attribute) || attribute.userEdited) {
            return true
        }
        if (
            attribute.id === BUSINESS_RULES_FIELD_ID ||
            attribute.dataType?.toLowerCase() === AttributeType.SEPARATOR
        ) {
            return false
        }
        return (
            isTemplatableAttribute(attribute) &&
            !TRIP_DERIVED_ATTRIBUTE_IDS.includes(attribute.id) &&
            !!(attribute as { required?: boolean }).required &&
            isBlank(attribute.value)
        )
    })

/**
 * Whether Quick create can use a template: the server says every required,
 * templatable attribute has a value, or the only ones missing are those the
 * form fills in from the Trip Number (Shipment Name), which a template cannot
 * hold anyway.
 */
export const isQuickCreateReady = (
    preset: Pick<
        EntityPreset,
        'quickCreateReady' | 'missingRequiredAttributeIds'
    >,
): boolean =>
    preset.quickCreateReady ||
    (preset.missingRequiredAttributeIds ?? []).every((id) =>
        TRIP_DERIVED_ATTRIBUTE_IDS.includes(id),
    )

/**
 * The mandatory fields a template needs before it can be saved: every
 * required, visible, editable field a template can hold that is still empty.
 * Trip Number, Unique fields and the fields filled from the Trip Number are
 * entered per record, so they are not asked for here.
 */
export const getMissingRequiredForTemplate = (
    attributes: PresetSourceAttribute[],
): string[] =>
    (attributes ?? [])
        .filter(
            (attribute) =>
                attribute.required &&
                !attribute.hide &&
                !attribute.disabled &&
                isTemplatableAttribute(attribute) &&
                isBlank(attribute.value),
        )
        .map((attribute) => attribute.id)
